import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, ColumnSelector, type ColumnDefinition, useColumnVisibility,
} from 'components/ui';
import { EnterpriseScreenShell, actionButtonFactory, useCan, useStandardConfirms } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { userAccessService } from 'api/services';
import type { AccessOptionsDto, UserAccessSummaryDto } from 'api/types';
import UserAccessModal, { type UserAccessModalMode } from 'components/modals/modules/settings/UserAccessModal';

// ============================================================================
// USER ACCESS (Settings) — who holds which roles, and where they may work:
// the whole company, or chosen entities with all or chosen business units and
// all or chosen financial years. Login stays with Keycloak / tenant-hub; a
// person appears here after their first login and waits for access.
// The list is the house list screen (search, list / card view, filters, column
// selector, ⋮ row actions); a person opens in a tabbed modal (Roles · Where
// they work · Sign-in) that minimizes to the bar.
// ============================================================================

type ViewMode = 'list' | 'grid';
type StatusFilter = '' | 'WAITING' | 'ACTIVE';
type ScopeFilter = '' | 'COMPANY' | 'ENTITIES';
type AccessTab = 'roles' | 'where' | 'signin';

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'name', label: 'Name' },
  { id: 'userName', label: 'User name' },
  { id: 'email', label: 'Email' },
  { id: 'roles', label: 'Roles' },
  { id: 'access', label: 'Works in' },
  { id: 'status', label: 'Status' },
  { id: 'lastLoginAt', label: 'Last sign-in' },
  { id: 'actions', label: 'Actions', required: true },
];

const fmtWhen = (s?: string | null) => (s ? new Date(s).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never');
const worksIn = (u: UserAccessSummaryDto) => (u.isTenantWide ? 'Whole company' : u.entityCount === 0 ? 'Nowhere' : `${u.entityCount} entit${u.entityCount === 1 ? 'y' : 'ies'}`);
const statusBadge = (u: UserAccessSummaryDto) => (u.waitingForAccess
  ? <span className="badge" style={{ background: '#d97706', color: '#fff', fontSize: '0.7rem' }}>Waiting</span>
  : <span className="badge" style={{ background: '#059669', color: '#fff', fontSize: '0.7rem' }}>Has access</span>);

interface UserAccessScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const UserAccessScreen: React.FC<UserAccessScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const can = useCan('user-access');
  const { confirmCustom, ConfirmDialogPortal } = useStandardConfirms();
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [users, setUsers] = useState<UserAccessSummaryDto[]>([]);
  const [options, setOptions] = useState<AccessOptionsDto | null>(null);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } = useColumnVisibility('user-access-columns', COLUMN_DEFS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [draft, setDraft] = useState({ status: '' as StatusFilter, roleId: '', scope: '' as ScopeFilter });
  const [applied, setApplied] = useState({ status: '' as StatusFilter, roleId: '', scope: '' as ScopeFilter });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<UserAccessModalMode>('view');
  const [modalUserId, setModalUserId] = useState<string | null>(null);
  const [modalTab, setModalTab] = useState<AccessTab>('roles');
  const minimizeRef = useRef<(() => void) | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setUsers(await userAccessService.getUsers()); }
    catch (err: any) { notificationService.toast.error(err?.response?.data?.message || 'Could not load the users.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    userAccessService.getOptions().then(setOptions)
      .catch(err => notificationService.toast.error(err?.response?.data?.message || 'Could not load the organisation.'));
  }, [load]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(u => (!applied.status || (applied.status === 'WAITING' ? u.waitingForAccess : !u.waitingForAccess))
      && (!applied.roleId || u.roles.some(r => r.id === applied.roleId))
      && (!applied.scope || (applied.scope === 'COMPANY' ? u.isTenantWide : !u.isTenantWide))
      && (!q || [u.fullName, u.userName, u.email, ...u.roles.map(r => r.name)].some(v => v?.toLowerCase().includes(q))));
  }, [users, search, applied]);

  const waiting = users.filter(u => u.waitingForAccess).length;

  const open = useCallback((mode: UserAccessModalMode, userId: string, tab: AccessTab = 'roles') => {
    const modalId = `${tabId}-user-access-modal-${userId}`;
    if (tabId && isMinimized(modalId)) {
      const m = getMinimizedModal(modalId);
      dismissMinimized(modalId); m?.onRestore?.();
      return;
    }
    if (modalOpen) minimizeRef.current?.();
    setModalMode(mode); setModalUserId(userId); setModalTab(tab); setModalOpen(true);
  }, [tabId, isMinimized, getMinimizedModal, dismissMinimized, modalOpen]);

  const handleRestore = useCallback((mode: UserAccessModalMode, userId: string) => {
    setModalMode(mode); setModalUserId(userId); setModalOpen(true);
  }, []);

  const removeAccess = useCallback(async (u: UserAccessSummaryDto) => {
    const ok = await confirmCustom({
      title: 'Remove all access?', variant: 'danger', confirmLabel: 'Remove access',
      message: `Remove every role and all access from ${u.fullName || u.userName}? They will still sign in but see nothing.`,
    });
    if (!ok) return;
    try {
      const d = await userAccessService.getUser(u.userId);
      await userAccessService.save(u.userId, { roleIds: [], isTenantWide: false, entities: [], accessStamp: d.accessStamp });
      notificationService.toast.success(`${u.fullName || u.userName} has no access now.`);
      load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not remove the access.');
    }
  }, [confirmCustom, load]);

  const rowActions = useCallback((u: UserAccessSummaryDto): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [{ id: 'view', label: 'View', icon: <IconSystem.eye size={18} />, onClick: () => open('view', u.userId) }];
    if (can('UPDATE')) {
      items.push({ id: 'edit', label: 'Edit access', icon: <IconSystem.edit size={18} />, onClick: () => open('edit', u.userId) });
      items.push({ id: 'where', label: 'Where they work', icon: <IconSystem.folder size={18} />, onClick: () => open('edit', u.userId, 'where') });
      if (!u.waitingForAccess) items.push({ id: 'remove', label: 'Remove all access', icon: <IconSystem.trash size={18} />, variant: 'danger', onClick: () => removeAccess(u) });
    }
    return items;
  }, [can, open, removeAccess]);

  const columns: SyutGridColumn<UserAccessSummaryDto & { id: string }>[] = useMemo(() => [
    { id: 'name', header: 'Name', size: 220,
      cell: ({ row }) => (
        <button type="button" onClick={() => open('view', row.original.userId)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--primary)', textAlign: 'left' }}>
          {row.original.fullName || row.original.userName}
        </button>
      ) },
    { id: 'userName', header: 'User name', accessorKey: 'userName', size: 210, cell: ({ row }) => row.original.userName || '—' },
    { id: 'email', header: 'Email', accessorKey: 'email', size: 230, cell: ({ row }) => row.original.email || '—' },
    { id: 'roles', header: 'Roles', size: 260, cell: ({ row }) => row.original.roles.map(r => r.name).join(', ') || '—' },
    { id: 'access', header: 'Works in', size: 140, cell: ({ row }) => worksIn(row.original) },
    { id: 'status', header: 'Status', size: 120, cell: ({ row }) => statusBadge(row.original) },
    { id: 'lastLoginAt', header: 'Last sign-in', size: 170, cell: ({ row }) => fmtWhen(row.original.lastLoginAt) },
  ], [open]);

  const rows = useMemo(() => shown.map(u => ({ ...u, id: u.userId })), [shown]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Card View' },
  ];

  const actionButtons: ActionButton[] = [actionButtonFactory.refresh(load)];
  const appliedCount = (applied.status ? 1 : 0) + (applied.roleId ? 1 : 0) + (applied.scope ? 1 : 0);
  const sel: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem' };

  return (
    <EnterpriseScreenShell
      title="User Access"
      description={`Give people their roles and the entities, business units and years they work in${waiting ? ` · ${waiting} waiting for access` : ''}`}
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.users size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: search, onChange: setSearch, placeholder: 'Search people or roles...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: m => setViewMode(m as ViewMode) }}
      filterConfig={{ onClick: () => { setDraft(applied); setFilterOpen(!filterOpen); }, isActive: filterOpen, appliedCount }}
      customToolbarItems={<ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns} onApply={applyColumns} onReset={resetColumns} />}
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Status</label>
              <select className="form-input" style={sel} value={draft.status} onChange={e => setDraft(d => ({ ...d, status: e.target.value as StatusFilter }))}>
                <option value="">All</option><option value="WAITING">Waiting for access</option><option value="ACTIVE">Has access</option>
              </select>
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Role</label>
              <select className="form-input" style={sel} value={draft.roleId} onChange={e => setDraft(d => ({ ...d, roleId: e.target.value }))}>
                <option value="">Any role</option>
                {options?.roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Works in</label>
              <select className="form-input" style={sel} value={draft.scope} onChange={e => setDraft(d => ({ ...d, scope: e.target.value as ScopeFilter }))}>
                <option value="">Anywhere</option><option value="COMPANY">The whole company</option><option value="ENTITIES">Chosen entities</option>
              </select>
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { const none = { status: '' as StatusFilter, roleId: '', scope: '' as ScopeFilter }; setDraft(none); setApplied(none); }}>Clear</button>
            <button className="btn btn-primary" onClick={() => { setApplied(draft); setFilterOpen(false); }}>Apply</button>
          </div>
        </div>
      )}
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={rows} columns={columns} visibleColumns={['actions', ...visibleColumns.filter(c => c !== 'actions')]} enablePagination defaultPageSize={25}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : applied.status === 'WAITING' ? 'Nobody is waiting. New people appear here after their first sign-in.' : 'No users match.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start' }}>
              {shown.map(u => (
                <div key={u.userId} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.userName}</div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0', cursor: 'pointer' }} onClick={() => open('view', u.userId)}>{u.fullName || u.userName}</h3>
                    </div>
                    <ActionMenu items={rowActions(u)} />
                  </div>
                  <div>{statusBadge(u)}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-primary)' }}>{u.roles.map(r => r.name).join(', ') || 'No role'}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{worksIn(u)} · last sign-in {fmtWhen(u.lastLoginAt)}</div>
                </div>
              ))}
              {shown.length === 0 && <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{loading ? 'Loading...' : 'No users match.'}</div>}
            </div>
          </div>
        )}
      </div>

      <UserAccessModal isOpen={modalOpen} mode={modalMode} userId={modalUserId} options={options} canEdit={can('UPDATE')}
        initialTab={modalTab}
        onEdit={() => setModalMode('edit')}
        onSaved={load}
        onClose={() => setModalOpen(false)}
        tabId={tabId} moduleName={moduleName || 'User Access'}
        minimizeRef={minimizeRef} onRestoreModal={handleRestore} />
    </EnterpriseScreenShell>
  );
};

export default UserAccessScreen;
